import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import TMC from '@autonomic/browser-sdk';
import AutoIntl from '@au/core/lib/components/elements/AutoIntl';
import AuDropDown from '@au/core/lib/components/elements/AuDropDown';
import AuButton, { BUTTON_TYPE_TERTIARY } from '@au/core/lib/components/elements/AuButton';
import Spinner from '@au/core/lib/components/elements/Spinner';
import Banner, { BANNER_TYPE_INFO_SQUARE } from '../../Banner';
import ConversionRulesPopout from '../../popout/ConversionRulesPopout';
import { createResponseAlertMessage } from '@au/core/lib/components/objects/AlertMessage';
import { FormattedMessage } from 'react-intl';

import fieldset from '../../../css/fieldset.module.scss';
import styles from '../../../css/components/entity/customInputs/source_live_id_input.module.scss';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

// using | has limitation. if sourceMessageName ever contains |, it will break the logic
// if possible in DCP Console, we should modify AuDropDown to accept val as both String and Object
// so we could pass something like { id: '123', name: 'abc'} without having to do string concatenation/parsing
const mapRulesToOptions = (items = []) =>
  items.map((item) => ({
    displayString: item.sourceMessageName,
    val: `${item.sourceMessageName}|${item.id}`,
    subTitle: item.id,
    className: styles.option,
  }));

export const SourceLiveIdInput = ({ entity, field, fieldChangeCallback, labelId, showErrors, validationErrors }) => {
  const { sourceEnv } = entity;

  const [fetching, setFetching] = useState(false);
  const [suggestedOptions, setSuggestedOptions] = useState([]);
  const [conversionRuleName, setConversionRuleName] = useState('');
  const [conversionRuleId, setConversionRuleId] = useState(undefined);
  const [showPopout, setShowPopout] = useState(false);

  const skipNextFetchRef = useRef(false);

  const error = validationErrors && field in validationErrors ? validationErrors[field] : null;

  const endpointRef = useRef(null);
  if (endpointRef.current === null) {
    endpointRef.current = new TMC.services.TelemetryDefinition().syncConfigs;
  }

  const debouncedFetchRef = useRef(null);
  if (debouncedFetchRef.current === null) {
    debouncedFetchRef.current = debounce((env, name, onSuccess, onError) => {
      setFetching(true);
      endpointRef.current
        .listAllRules(env, { messageName: name })
        .then((resp) => {
          setFetching(false);
          onSuccess(resp);
        })
        .catch((err) => {
          setFetching(false);
          onError(err);
        });
    }, DEBOUNCE_MS);
  }

  useEffect(() => {
    let cancelled = false;

    // skip api call if name changed are from option selection, not name typing
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    const trimmedName = conversionRuleName.trim();

    if (trimmedName.length >= MIN_QUERY_LENGTH) {
      debouncedFetchRef.current(
        sourceEnv,
        trimmedName,
        (resp) => {
          if (!cancelled) {
            setSuggestedOptions(mapRulesToOptions(resp?.data?.items));
          }
        },
        (err) => {
          if (!cancelled) {
            setSuggestedOptions([]);
            createResponseAlertMessage(err);
          }
        },
      );
    } else {
      debouncedFetchRef.current.cancel();
      setSuggestedOptions([]);
    }

    return () => {
      cancelled = true;
      debouncedFetchRef.current.cancel();
    };
  }, [conversionRuleName, sourceEnv]);

  const handleNameChange = useCallback((name) => {
    setConversionRuleName(name);
    setConversionRuleId(undefined);
  }, []);

  const handleSelectOption = useCallback((selected) => {
    // to prevent API call from name changed in search/lookup useEffect
    skipNextFetchRef.current = true;

    const [name, id] = selected.split('|');
    setConversionRuleName(name);
    setConversionRuleId(id);
  }, []);

  useEffect(() => {
    fieldChangeCallback(field, conversionRuleId);
  }, [field, fieldChangeCallback, conversionRuleId]);

  const openPopout = useCallback(() => setShowPopout(true), []);
  const closePopout = useCallback(() => setShowPopout(false), []);

  return (
    <div className={fieldset.row}>
      <label className={fieldset.label}>
        <FormattedMessage id={labelId} />
      </label>
      <div className={fieldset.control}>
        <div className={styles.container}>
          <Banner type={BANNER_TYPE_INFO_SQUARE} className={styles.banner}>
            <div className={styles.banner_content}>
              <AutoIntl className={styles.banner_text} displayId='au.telemetry.sync.liveId.banner' tag={'span'} />
              <AuButton
                type={BUTTON_TYPE_TERTIARY}
                className={styles.banner_button}
                displayId='au.entity.title.conversion'
                onClick={openPopout}
              />
            </div>
          </Banner>

          <div className={styles.conversion_rule_container}>
            <AuDropDown
              className={styles.conversion_rule}
              allowTyping={true}
              onChange={handleNameChange}
              selection={conversionRuleName}
              selectOption={handleSelectOption}
              options={suggestedOptions}
              maxOptions={20}
              placeholderId='au.telemetry.sync.liveId.placeholder'
              showError={showErrors}
              error={error}
            />
            {fetching && <Spinner className={styles.spinner} />}
          </div>
        </div>
      </div>
      {showPopout && <ConversionRulesPopout sourceEnv={sourceEnv} onClose={closePopout} />}
    </div>
  );
};
